import { DataConnectionType } from "../dataConnectionTypes";
import { SqlDialect, quoteSqlIdentifier } from "../sql/dialects";
import { sqlClause } from "../sql/generation";

interface GenerateTableNameOptions {
  dataConnectionType: DataConnectionType;
  tablePath: string[];
}

/**
 * Returns a list of strings representing the path to a table. If the data
 * connection is multi-database, then include the database name
 */
export function getTablePath({
  databaseName,
  includeDatabaseName,
  schemaName,
  tableName,
}: {
  includeDatabaseName: boolean;
  tableName: string;
  schemaName: string;
  databaseName?: string | null;
}): string[] {
  const path = [schemaName, tableName.replaceAll(" ", "_")];
  if (includeDatabaseName && databaseName) {
    path.unshift(databaseName);
  }
  return path;
}

export function getSchemaPath({
  databaseName,
  includeDatabaseName,
  schemaName,
}: {
  includeDatabaseName: boolean;
  schemaName: string;
  databaseName?: string | null;
}): string[] {
  const path = [schemaName];
  if (includeDatabaseName && databaseName) {
    path.unshift(databaseName);
  }
  return path;
}

export const generateTableNameSQL = ({
  dataConnectionType,
  tablePath,
}: GenerateTableNameOptions): string => {
  return tablePath
    .map((id) => quoteSqlIdentifier(id, dataConnectionType))
    .join(".");
};

/**
 * Use this util every where we are generating a "preview/limit" query to ensure
 * that the query syntax is consistent to optimize caching.
 */
export const buildLimitedQuery = ({
  columns,
  connectionType,
  limit = 100,
  tablePath,
}: {
  tablePath: string;
  connectionType: DataConnectionType;
  columns?: Array<{ name: string; scalar?: string }>;
  limit?: number;
}): string => {
  const columnClause = columns
    ? columns
        .map((c) => {
          const colName = quoteSqlIdentifier(c.name, connectionType);
          const maybeScalar = c.scalar == null ? "" : `'${c.scalar}' as `;
          return `\n  ${maybeScalar}${colName}`;
        })
        .join(",") + "\n"
    : "* ";
  const limitChar = columns ? "\n" : " ";

  switch (connectionType) {
    // SQL server does not support the standard LIMIT syntax.
    case DataConnectionType.sqlserver:
    case DataConnectionType.cloudsql__sqlserver:
      return `select top ${limit} ${columnClause}from ${tablePath}`;
    default: {
      return `select ${columnClause}from ${tablePath}${limitChar}limit ${limit}`;
    }
  }
};

interface GenerateSelectOptions {
  dataConnectionType: DataConnectionType;
  tablePath: string[];
  columnNames: string[];
}

export const generateSelectColumnsSQL = ({
  columnNames,
  dataConnectionType,
  tablePath,
}: GenerateSelectOptions): string => {
  return [
    ...sqlClause(
      "select",
      columnNames.map((colName) =>
        quoteSqlIdentifier(colName, dataConnectionType),
      ),
    ),
    `from ${generateTableNameSQL({ dataConnectionType, tablePath })}`,
  ].join("\n");
};

export const buildLimitOffsetClause = ({
  dialect,
  limit,
  offset,
}: {
  dialect: SqlDialect;
  limit: number;
  offset: number;
}): string => {
  switch (dialect) {
    case "sqlserver":
    case "cloudsql__sqlserver":
      return `OFFSET ${offset} ROWS FETCH NEXT ${limit} ROWS ONLY`;
    case "athena":
    case "trino":
      return `OFFSET ${offset} LIMIT ${limit}`;
    default:
      return `LIMIT ${limit} OFFSET ${offset}`;
  }
};
